/**
 * Single source of truth for severity → Tailwind class mappings used across
 * validation UI (EditableField, ValidationBanner, ValidationSeverityIcon)
 * and feedback UI (AlertBanner).
 *
 * Add or change a severity's visual treatment here — every consumer reads
 * from `SEVERITY_CONFIG` (or one of the `severity*` helpers below).
 *
 * All class strings are full literals so Tailwind's content scanner can
 * statically detect them.
 */

import { Severity } from '@insio/validation';

export { Severity };

/** Wider alert severity — adds `success` for confirmation banners. */
export type AlertSeverity = Severity | 'success' | 'info';

/** A nullable validation severity — most consumers want to handle "no issue" too. */
export type SeverityOrNull = Severity | null | undefined;

export type SeverityIconName =
  'circle-xmark' | 'triangle-exclamation' | 'circle-check' | 'circle-info';

export interface SeverityStyle {
  /** FontAwesome icon name. */
  icon: SeverityIconName;
  /** Foreground color class for the icon and inline text. */
  iconColor: string;
  /** Background-tint class for cards / banners. */
  bgColor: string;
  /** Full 2px semantic border (color + width) — for inputs / cards. */
  border: string;
  /** Just the border color class — combine with your own width. */
  borderColor: string;
  /** Maps to the Badge component's `color` prop. */
  badgeColor: AlertSeverity;
  /** Slightly muted body-text class — pairs with `iconColor` for the title. */
  bodyColor: string;
}

export const SEVERITY_CONFIG: Record<AlertSeverity, SeverityStyle> = {
  error: {
    icon: 'circle-xmark',
    iconColor: 'text-error-text',
    bgColor: 'bg-error-bg',
    border: 'border-2 border-error-border',
    borderColor: 'border-error-border',
    badgeColor: 'error',
    bodyColor: 'text-error-text/80'
  },
  warning: {
    icon: 'triangle-exclamation',
    iconColor: 'text-warn-text',
    bgColor: 'bg-warn-bg',
    border: 'border-2 border-warn-border',
    borderColor: 'border-warn-border',
    badgeColor: 'warning',
    bodyColor: 'text-warn-text/80'
  },
  success: {
    icon: 'circle-check',
    iconColor: 'text-success-text',
    bgColor: 'bg-success-bg',
    border: 'border-2 border-success-border',
    borderColor: 'border-success-border',
    badgeColor: 'success',
    bodyColor: 'text-success-text/80'
  },
  info: {
    icon: 'circle-info',
    iconColor: 'text-info-text',
    bgColor: 'bg-info-bg',
    border: 'border-2 border-info-border',
    borderColor: 'border-info-border',
    badgeColor: 'info',
    bodyColor: 'text-info-text/80'
  }
};

/** Resolves a validation severity to its style entry, or `null` for non-validation values. */
export function severityStyle(severity: SeverityOrNull): SeverityStyle | null {
  if (severity === Severity.error) return SEVERITY_CONFIG.error;
  if (severity === Severity.warning) return SEVERITY_CONFIG.warning;
  if (severity === Severity.info) return SEVERITY_CONFIG.info;
  return null;
}
